import { createSignal, Show, createMemo, createEffect } from "solid-js";
import { useParams, useNavigate } from "@solidjs/router";
import { useQuizContext } from "./QuizContext";
import QuizQuestionPreview from "./QuizQuestionPreview";
import { parseQuizMarkdown } from "../shared/quizParser";
import {
  DEFAULT_QUIZ_JOIN_METHODS,
  DEFAULT_MULTIPLE_CHOICE_SCORING,
  enabledJoinMethodsCount,
  getMultipleChoiceScoring,
  getQuizJoinMethods,
  type MultipleChoiceScoring,
  type QuizJoinMethod,
  type QuizJoinMethods,
  updateQuizSettingsJoinMethods,
  updateQuizSettingsScoring
} from "../shared/quizSettings";

export default function QuizEditor() {
  const params = useParams();
  const navigate = useNavigate();
  const [quizStore, , quizActions] = useQuizContext() as any;
  
  const [title, setTitle] = createSignal('');
  const [quizId, setQuizId] = createSignal('');
  const [markdown, setMarkdown] = createSignal('');
  const [settingsRaw, setSettingsRaw] = createSignal<string | null>(null);
  const [joinMethods, setJoinMethods] = createSignal<QuizJoinMethods>({ ...DEFAULT_QUIZ_JOIN_METHODS });
  const [multipleChoiceScoring, setMultipleChoiceScoring] = createSignal<MultipleChoiceScoring>({ ...DEFAULT_MULTIPLE_CHOICE_SCORING });
  const [penaltyInput, setPenaltyInput] = createSignal('1');
  const [hideQuestionsFromStudents, setHideQuestionsFromStudents] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [notFound, setNotFound] = createSignal(false);
  const [cursorPosition, setCursorPosition] = createSignal(0);
  const [showHelp, setShowHelp] = createSignal(false);
  const [joinMethodsError, setJoinMethodsError] = createSignal<string | null>(null);
  const [scoringError, setScoringError] = createSignal<string | null>(null);
  let textareaRef: HTMLTextAreaElement | undefined;

  createEffect(() => {
    const id = parseInt(params.id ?? '');
    const quiz = quizStore.quizzes.find((q: any) => q.id === id);
    
    if (quiz) {
      setTitle(quiz.title);
      setQuizId(quiz.quizId);
      setMarkdown(quiz.markdown);
      setSettingsRaw(quiz.settings || null);
      setJoinMethods(getQuizJoinMethods(quiz.settings));
      const scoring = getMultipleChoiceScoring(quiz.settings);
      setMultipleChoiceScoring(scoring);
      setPenaltyInput(String(scoring.wrongAnswerPenalty));
      setJoinMethodsError(null);
      setScoringError(null);
      setHideQuestionsFromStudents(quiz.hideQuestionsFromStudents || false);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  });

  const toggleJoinMethod = (method: QuizJoinMethod) => {
    const current = joinMethods();
    const next = {
      ...current,
      [method]: !current[method]
    } as QuizJoinMethods;

    if (enabledJoinMethodsCount(next) === 0) {
      setJoinMethodsError('At least one join method must remain enabled.');
      return;
    }

    setJoinMethods(next);
    setJoinMethodsError(null);
  };

  const handleSave = async () => {
    if (enabledJoinMethodsCount(joinMethods()) === 0) {
      setJoinMethodsError('At least one join method must be enabled.');
      return;
    }
    const penalty = Number(penaltyInput());
    if (multipleChoiceScoring().mode === 'partial' &&
      (!penaltyInput().trim() || !Number.isFinite(penalty) || penalty < 0 || penalty > 10)) {
      setScoringError('Enter a penalty between 0 and 10.');
      return;
    }

    setLoading(true);
    const id = parseInt(params.id ?? '');
    const scoring = multipleChoiceScoring();
    try {
      await quizActions.updateQuiz(id, {
        title: title(),
        quizId: quizId(),
        markdown: markdown(),
        settings: updateQuizSettingsScoring(
          updateQuizSettingsJoinMethods(settingsRaw(), joinMethods()),
          { ...scoring, wrongAnswerPenalty: scoring.mode === 'partial' ? penalty : scoring.wrongAnswerPenalty }
        ),
        hideQuestionsFromStudents: hideQuestionsFromStudents()
      });
      navigate('/manage-quizzes');
    } catch (error: any) {
      alert('Error saving quiz: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/manage-quizzes');
  };

  // Parse markdown and find current question based on cursor position
  const parsedQuiz = createMemo(() => parseQuizMarkdown(markdown()));
  
  const currentQuestionIndex = createMemo(() => {
    const text = markdown();
    const cursor = cursorPosition();
    
    // Find which question block the cursor is in by counting --- separators
    const beforeCursor = text.substring(0, cursor);
    const separatorCount = (beforeCursor.match(/\n---\n/g) || []).length;
    
    return separatorCount;
  });

  const currentQuestion = createMemo(() => {
    const questions = parsedQuiz().questions;
    const index = currentQuestionIndex();
    return questions[index] || null;
  });

  const handleTextareaInput = (e: InputEvent & { currentTarget: HTMLTextAreaElement }) => {
    setMarkdown(e.currentTarget.value);
    setCursorPosition(e.currentTarget.selectionStart);
  };

  const handleTextareaClick = (e: MouseEvent & { currentTarget: HTMLTextAreaElement }) => {
    setCursorPosition(e.currentTarget.selectionStart);
  };

  const handleTextareaKeyUp = (e: KeyboardEvent & { currentTarget: HTMLTextAreaElement }) => {
    setCursorPosition(e.currentTarget.selectionStart);
  };

  return (
    <div>
      <Show when={notFound()}>
        <div class="alert alert-danger">
          Quiz not found. <a href="/manage-quizzes">Return to quiz list</a>
        </div>
      </Show>

      <Show when={!notFound()}>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h2>Edit Quiz</h2>
          <div>
            <button 
              type="button" 
              class="btn btn-secondary me-2"
              onClick={handleCancel}
              disabled={loading()}
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary"
              onClick={handleSave}
              disabled={loading()}
            >
              {loading() ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div class="mb-3">
            <label class="form-label">Title</label>
            <input
              type="text"
              class="form-control"
              value={title()}
              onInput={(e) => setTitle(e.currentTarget.value)}
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Quiz Code</label>
            <input
              type="text"
              class="form-control"
              value={quizId()}
              onInput={(e) => setQuizId(e.currentTarget.value.toUpperCase())}
              pattern="[A-Z0-9]{4,10}"
              title="4-10 uppercase letters or numbers"
              required
            />
            <small class="form-text text-muted">
              Students will use this code to join the quiz. State is automatically updated when you show/hide questions.
            </small>
          </div>

          <div class="mb-3">
            <label class="form-label">Allowed Join Methods</label>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="join-method-cas"
                checked={joinMethods().cas}
                onChange={() => toggleJoinMethod('cas')}
              />
              <label class="form-check-label" for="join-method-cas">
                CAS login
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="join-method-named"
                checked={joinMethods().named}
                onChange={() => toggleJoinMethod('named')}
              />
              <label class="form-check-label" for="join-method-named">
                Student with name
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="join-method-anonymous"
                checked={joinMethods().anonymous}
                onChange={() => toggleJoinMethod('anonymous')}
              />
              <label class="form-check-label" for="join-method-anonymous">
                Anonymous student (random name)
              </label>
            </div>
            <Show when={joinMethodsError()}>
              <div class="text-danger mt-1">{joinMethodsError()}</div>
            </Show>
            <small class="form-text text-muted">
              Students joining via <code>/q/&lt;code&gt;</code> will only see these login methods.
            </small>
          </div>

          <fieldset class="mb-3">
            <legend class="form-label fs-6">Multiple-choice scoring</legend>
            <div class="form-check">
              <input class="form-check-input" type="radio" name="multiple-choice-scoring"
                id="scoring-exact" checked={multipleChoiceScoring().mode === 'exact'}
                onChange={() => setMultipleChoiceScoring({ ...multipleChoiceScoring(), mode: 'exact' })} />
              <label class="form-check-label" for="scoring-exact">Exact match: 1 point only when the selected choices match the correct answers</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" name="multiple-choice-scoring"
                id="scoring-partial" checked={multipleChoiceScoring().mode === 'partial'}
                onChange={() => setMultipleChoiceScoring({ ...multipleChoiceScoring(), mode: 'partial' })} />
              <label class="form-check-label" for="scoring-partial">Partial credit</label>
            </div>
            <Show when={multipleChoiceScoring().mode === 'partial'}>
              <div class="mt-2 ms-4" style={{ "max-width": "22rem" }}>
                <label class="form-label" for="wrong-answer-penalty">Penalty per wrong choice</label>
                <input id="wrong-answer-penalty" class="form-control" type="number" min="0" max="10" step="any"
                  value={penaltyInput()}
                  onInput={(e) => {
                    setPenaltyInput(e.currentTarget.value);
                    setScoringError(null);
                  }} />
                <small class="form-text text-muted">
                  Each correct choice gives 1 share; each wrong choice subtracts this many shares.
                  Divide by the number of correct choices and limit the result to 0–1 point.
                  A penalty of 0 ignores wrong choices; 1 cancels one correct choice.
                </small>
              </div>
            </Show>
            <Show when={scoringError()}><div class="text-danger mt-1">{scoringError()}</div></Show>
            <small class="form-text text-muted">Single-choice questions always use exact scoring. Existing quizzes use exact scoring until changed here.</small>
          </fieldset>

          <div class="row mb-3">
            <div class="col-md-6">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label mb-0">Markdown Content</label>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-info"
                  onClick={() => setShowHelp(!showHelp())}
                >
                  {showHelp() ? 'Hide Help' : 'Show Help'}
                </button>
              </div>
              
              <Show when={showHelp()}>
                <div class="alert alert-info" style={{ "font-size": "13px" }}>
                  <strong>Quiz Markdown Syntax:</strong>
                  <hr />
                  <p class="mb-2"><strong>Separate questions:</strong> Use <code>---</code> on its own line</p>
                  
                  <p class="mb-2"><strong>Single choice:</strong></p>
                  <pre class="mb-2" style={{ "font-size": "12px", "background": "#fff", "padding": "8px" }}>
{`
# Question Title

Some other text here.

:choice:
- Wrong answer
X Correct answer
- Another wrong answer`}
                  </pre>
                  
                  <p class="mb-2"><strong>Multiple choice:</strong></p>
                  <pre class="mb-2" style={{ "font-size": "12px", "background": "#fff", "padding": "8px" }}>
{`
# Question Title

:choices:
- Wrong answer
X Correct answer 1
X Correct answer 2
- Wrong answer`}
                  </pre>
                  
                  <p class="mb-2"><strong>Formatting:</strong></p>
                  <ul class="mb-2" style={{ "font-size": "12px" }}>
                    <li>Use <code>X</code> for correct answers, <code>-</code> for wrong ones</li>
                    <li>Use <strong>**bold**</strong>, <em>*italic*</em>, and other markdown in questions</li>
                    <li>Add code blocks with triple backticks: <code>```python</code></li>
                    <li>Add math formulas: <code>$x^2$</code> (inline) or <code>$$x^2$$</code> (display)</li>
                  </ul>
                </div>
              </Show>
              
              <textarea
                ref={textareaRef}
                class="form-control font-monospace"
                rows={20}
                value={markdown()}
                onInput={handleTextareaInput}
                onClick={handleTextareaClick}
                onKeyUp={handleTextareaKeyUp}
                required
                style={{ "font-size": "14px" }}
              />
              <small class="form-text text-muted">
                Write your quiz questions and answers in Markdown format
              </small>
            </div>

            <div class="col-md-6">
              <label class="form-label">Preview</label>
              <div class="border rounded p-3" style={{ "min-height": "400px", "background-color": "#f8f9fa" }}>
                <Show when={currentQuestion()} fallback={
                  <div class="text-muted text-center mt-5">
                    <p>Position your cursor in the markdown editor to preview a question</p>
                    <small>Questions are separated by ---</small>
                  </div>
                }>
                  <QuizQuestionPreview 
                    question={currentQuestion()!} 
                    readonly={false}
                    onSelect={(answers) => console.log('Preview selected:', answers)}
                  />
                </Show>
                
                <Show when={parsedQuiz().errors.length > 0}>
                  <div class="alert alert-warning mt-3">
                    <strong>Parser Warnings:</strong>
                    <ul class="mb-0 mt-2">
                      {parsedQuiz().errors.map(error => (
                        <li>{error}</li>
                      ))}
                    </ul>
                  </div>
                </Show>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="hideQuestionsFromStudents"
                checked={hideQuestionsFromStudents()}
                onChange={(e) => setHideQuestionsFromStudents(e.currentTarget.checked)}
              />
              <label class="form-check-label" for="hideQuestionsFromStudents">
                Hide questions from students
              </label>
            </div>
            <small class="form-text text-muted">
              If enabled, students won't see the questions on their page. They will see them only on the public display (teacher's slides).
            </small>
          </div>
        </form>
      </Show>
    </div>
  );
}
